import React, { useRef } from 'react';
import { useEffect, useState } from "react";
import EmployeeForm from "../../components/employeeForm/employeeForm";
import EmployeePage from "../EmployeePage/EmployeePage";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import { canDeleteEmployee } from "../../utils/hrRequestApproval";
const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

/** Legacy rows may have stored the manager's employee_id in reporting_to */
const looksLikeGuid = (v) =>
  typeof v === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v.trim()
  );

// Employee management component with enhanced form
const EmployeeManagement = ({
  userProfile,
  currentView,
  pendingNotificationAction,
  onNotificationActionHandled,
}) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [predefinedPositions, setPredefinedPositions] = useState([]);
  const EMPTY_EMPLOYEE = {
    name: '',
    position: '',
    department: '',
    joining_date: '',
    email: '',
    phone: '',
    salary: '',
    passport_number: '',
    grade: '1',
    group: 'Staff',
    status: 'Active',
    location: 'Kuwait',
    manager_id: '',
    role: 'Staff',
    shift_id: '',
  };

  const [newEmployee, setNewEmployee] = useState({ ...EMPTY_EMPLOYEE });
  const [shiftOptions, setShiftOptions] = useState([]);
  const [defaultShiftId, setDefaultShiftId] = useState(null);
  const [defaultShiftName, setDefaultShiftName] = useState('');

  useEffect(() => {
    if (!showAddForm && !showEditForm) {
      setNewEmployee({ ...EMPTY_EMPLOYEE });
      setEditingEmployee(null);
    }
  }, [showAddForm, showEditForm]);
  const [page, setPage] = useState(0); // 0-based index
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [total, setTotal] = useState(0);
  const [advancedSearch, setAdvancedSearch] = useState({});
  const debounceTimer = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportingMissingFace, setExportingMissingFace] = useState(false);
  const [missingFaceExportError, setMissingFaceExportError] = useState(null);

  // Utility function for currency formatting
  const formatCurrency = (amount) => {
    if (amount == null) return 'KD 0.000';
    return `KD ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
  };
  // Utility function for role display names
  const getRoleDisplayName = (role) => {
    if (role === null || role === undefined) return 'Staff';

    // Handle numeric role codes from Dynamics 365
    const roleMap = {
      100000000: 'Staff',
      100000001: 'Manager',
      100000002: 'Admin',
      100000003: 'Super'
    };

    // If it's a number, convert to string and look up
    if (typeof role === 'number') {
      return roleMap[role] || 'Staff';
    }

    // If it's already a string, return as is or capitalize first letter
    if (typeof role === 'string') {
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    }

    return 'Staff';
  };

  // Utility function for role colors
  const getRoleColor = (role) => {
    const displayName = getRoleDisplayName(role);
    const colorMap = {
      'Staff': 'staff',
      'Manager': 'manager',
      'Admin': 'admin',
      'Super': 'super'
    };
    return colorMap[displayName] || 'staff';
  };

  useEffect(() => {
    const handler = setTimeout(() => {
    if (userProfile?.role !== 'staff') {
      fetchEmployees();
    }
    }, 300);
    return () => clearTimeout(handler);
  }, [page, limit, sortBy, sortOrder, advancedSearch]);

  const handleSearchChange = (value) => {
     clearTimeout(debounceTimer.current);
     debounceTimer.current = setTimeout(() => {
    if (userProfile?.role !== 'staff') {
      fetchEmployees(value);
    }
    }, 500);
};

  const fetchEmployees = async (searchQuery = '') => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const params = {
        skip: page * limit,
        limit,
        search: searchQuery,
        sort_by: sortBy,
        sort_order: sortOrder,
        // Advanced search params (only these three)
        ...(advancedSearch.status ? { status: advancedSearch.status } : {}),
        ...(advancedSearch.employee_no ? { employee_no: advancedSearch.employee_no } : {}),
        ...(advancedSearch.name ? { name: advancedSearch.name } : {}),
      };
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers,
        params,
      });
      setEmployees(response.data.employees || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching employees:', error);
      // Handle authentication errors
      if (error.message && error.message.includes('sign in')) {
        alert('Authentication required. Please sign in with Microsoft.');
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableManagers = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employees/managers`, {
        headers
      });
      setAvailableManagers(response.data.managers || []);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchPredefinedPositions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system/positions`);
      setPredefinedPositions(response.data.positions || []);
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };

  const fetchShiftOptions = async () => {
    if (userProfile?.role !== 'super') return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/shifts/options`,
        { headers }
      );
      setShiftOptions(response.data.shifts || []);
      setDefaultShiftId(response.data.default_shift_id || null);
      setDefaultShiftName(response.data.default_shift_name || '');
    } catch (error) {
      console.error('Error fetching shift options:', error);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'super') {
      fetchShiftOptions();
    }
  }, [userProfile?.role]);

  const buildEmployeePayload = (employeeData) => {
    const payload = {
      ...employeeData,
      joining_date: new Date(employeeData.joining_date).toISOString(),
      salary: parseFloat(employeeData.salary) || 0,
      grade: parseInt(employeeData.grade, 10),
      manager_id: employeeData.manager_id || null,
      role: employeeData.role,
    };
    if (userProfile?.role === 'super') {
      payload.shift_id = employeeData.shift_id || null;
    }
    return payload;
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees`,
        buildEmployeePayload(newEmployee),
        { headers }
      );
      setShowAddForm(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error adding employee:', error);
      alert('Error adding employee. Please check your permissions.');
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setNewEmployee({
      name: employee.name,
      position: employee.position,
      department: employee.department,
      joining_date: employee.joining_date ? new Date(employee.joining_date).toISOString().split('T')[0] : '',
      email: employee.email,
      phone: employee.phone || '',
      salary: employee.salary?.toString() || '',
      passport_number: employee.passport_number || '',
      grade: employee.grade?.toString() || '1',
      group: employee.group || 'Staff',
      status: employee.status || 'Active',
      location: employee.location || 'Kuwait',
      manager_id:
        employee.manager_id ||
        (looksLikeGuid(employee.reporting_to) ? employee.reporting_to : '') ||
        '',
      role: getRoleDisplayName(employee.role) || 'Staff',
      shift_id: employee.shift_id || '',
    });
    setShowEditForm(true);
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${editingEmployee.id}`,
        buildEmployeePayload(newEmployee),
        { headers }
      );
      setEditingEmployee(null);
      setShowEditForm(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee:', error);
      alert('Error updating employee. Please check your permissions.');
    }
  };

  const handleRowStatusUpdate = async (employee, nextStatus) => {
    const employeeId = employee?.id || employee?.employee_id;
    if (!employeeId) return;
    const normalizedStatus = nextStatus === 'Inactive' ? 'Exited' : nextStatus;
    try {
      const headers = await getAuthHeaders();
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeId}/status`,
        { status: normalizedStatus },
        { headers }
      );
      setEmployees((prev) =>
        prev.map((item) =>
          (item.id || item.employee_id) === employeeId
            ? { ...item, status: normalizedStatus }
            : item
        )
      );
    } catch (error) {
      console.error('Error updating employee status:', error);
      throw error;
    }
  };

  const buildDeleteConfirmMessage = (employee) => {
    const name = employee?.name || 'this employee';
    return (
      `Delete ${name} permanently?\n\n`
      + 'This will remove the employee and all related HR requests, attendance records, '
      + 'payslips, and notifications from the portal. This cannot be undone.'
    );
  };

  const handleDeleteEmployee = async (employee, { onSuccess } = {}) => {
    const employeeId = employee?.id || employee?.employee_id;
    if (!employeeId) return;
    if (!window.confirm(buildDeleteConfirmMessage(employee))) return;

    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeId}`,
        { headers }
      );
      onSuccess?.();
      await fetchEmployees();
      alert('Employee deleted successfully.');
    } catch (error) {
      console.error('Error deleting employee:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join('\n')
          : 'Error deleting employee. Please try again.';
      alert(message);
    }
  };

  // Handler for advanced search modal
  const handleAdvancedSearch = (fields) => {
    setAdvancedSearch(fields);
    setPage(0);
    // fetchEmployees will be triggered by useEffect
  };

  const parseExportError = async (err) => {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed.detail === 'string') return parsed.detail;
      } catch (_) {
        /* ignore */
      }
    }
    if (typeof data?.detail === 'string') return data.detail;
    return 'Failed to export employees. Please try again.';
  };

  const handleExportEmployees = async () => {
    try {
      setExporting(true);
      setExportError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/export`,
        {
          headers,
          params: {
            search: search || undefined,
            sort_by: sortBy,
            sort_order: sortOrder,
            ...(advancedSearch.status ? { status: advancedSearch.status } : {}),
            ...(advancedSearch.employee_no ? { employee_no: advancedSearch.employee_no } : {}),
            ...(advancedSearch.name ? { name: advancedSearch.name } : {}),
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/i);
      link.download = match ? match[1].trim() : 'employees_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting employees:', err);
      setExportError(await parseExportError(err));
    } finally {
      setExporting(false);
    }
  };

  const handleExportMissingCheckinFace = async () => {
    try {
      setExportingMissingFace(true);
      setMissingFaceExportError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/export-missing-checkin-face`,
        {
          headers,
          params: {
            sort_by: sortBy,
            sort_order: sortOrder,
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/i);
      link.download = match ? match[1].trim() : 'employees_missing_checkin_face.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting employees missing check-in face:', err);
      setMissingFaceExportError(await parseExportError(err));
    } finally {
      setExportingMissingFace(false);
    }
  };

  const canExportEmployees = ['admin', 'super', 'manager'].includes(userProfile?.role);

  const getPageTitle = () => {
    switch (userProfile?.role) {
      case 'admin':
      case 'super': return 'All Employees';
      case 'manager': return 'My Team';
      case 'staff': return 'My Profile';
      default: return 'Employees';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'active',
      'On Leave': 'on-leave',
      'Exited': 'exited',
      'Inactive': 'exited',
    };
    return colors[status] || 'active';
  };

  const getGroupColor = (group) => {
    const colors = {
      'FOH': 'foh',
      'Management': 'management',
      'Staff': 'staff',
      'BOH': 'boh'
    };
    return colors[group] || 'staff';
  };

  return (
    <div className="employee-management w-full min-w-0">
      {showAddForm && (
        <EmployeeForm
          isEdit={false}
          newEmployee={newEmployee}
          setNewEmployee={setNewEmployee}
          predefinedPositions={predefinedPositions}
          availableManagers={availableManagers}
          handleAddEmployee={handleAddEmployee}
          handleUpdateEmployee={handleUpdateEmployee}
          setShowAddForm={setShowAddForm}
          setShowEditForm={setShowEditForm}
          setEditingEmployee={setEditingEmployee}
          userProfile={userProfile}
          shiftOptions={shiftOptions}
          defaultShiftName={defaultShiftName}
        />
      )}

      {showEditForm && (
        <EmployeeForm
          isEdit={true}
          newEmployee={newEmployee}
          setNewEmployee={setNewEmployee}
          predefinedPositions={predefinedPositions}
          availableManagers={availableManagers}
          handleAddEmployee={handleAddEmployee}
          handleUpdateEmployee={handleUpdateEmployee}
          setShowAddForm={setShowAddForm}
          setShowEditForm={setShowEditForm}
          setEditingEmployee={setEditingEmployee}
          userProfile={userProfile}
          shiftOptions={shiftOptions}
          defaultShiftName={defaultShiftName}
        />
      )}

      <EmployeePage
        employees={employees}
        userProfile={userProfile}
        handleEditEmployee={handleEditEmployee}
        formatCurrency={formatCurrency}
        getRoleDisplayName={getRoleDisplayName}
        getRoleColor={getRoleColor}
        getGroupColor={getGroupColor}
        onSearchChange={handleSearchChange}
        getStatusColor={getStatusColor}
        getPageTitle={getPageTitle}
        setShowAddForm={setShowAddForm}
        page={page}
        limit={limit}
        total={total}
        setPage={setPage}
        setLimit={setLimit}
        search={search}
        setSearch={setSearch}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        loading={loading}
        onAdvancedSearch={handleAdvancedSearch}
        advancedSearchValues={advancedSearch}
        currentView={currentView}
        pendingNotificationAction={pendingNotificationAction}
        onNotificationActionHandled={onNotificationActionHandled}
        onExportEmployees={handleExportEmployees}
        exporting={exporting}
        exportError={exportError}
        onExportMissingCheckinFace={handleExportMissingCheckinFace}
        exportingMissingFace={exportingMissingFace}
        missingFaceExportError={missingFaceExportError}
        canExportEmployees={canExportEmployees}
        canDeleteEmployee={canDeleteEmployee(userProfile)}
        onDeleteEmployee={handleDeleteEmployee}
        onRowStatusUpdate={handleRowStatusUpdate}
      />
    </div>
  );
};

export default EmployeeManagement;